/**
 * Client-side PostHog `$exception` noise filter.
 * Keeps `posthog-browser.ts` free of testable predicate logic (and avoids pulling
 * `"use client"` / `posthog-js` into bun tests).
 */

export type PosthogExceptionFrame = {
	filename?: string | null;
	function?: string | null;
	in_app?: boolean | null;
	lineno?: number | null;
	colno?: number | null;
	[key: string]: unknown;
};

export type PosthogExceptionItem = {
	type?: string | null;
	value?: string | null;
	stacktrace?: {
		frames?: PosthogExceptionFrame[] | null;
		type?: string | null;
		[key: string]: unknown;
	} | null;
	mechanism?: {
		handled?: boolean | null;
		synthetic?: boolean | null;
		type?: string | null;
		[key: string]: unknown;
	} | null;
	[key: string]: unknown;
};

export type PosthogExceptionEventLike = {
	event?: string | null;
	properties?: {
		$exception_list?: PosthogExceptionItem[] | null;
		$exception_values?: unknown;
		$exception_types?: unknown;
		[key: string]: unknown;
	} | null;
};

const INJECTED_GLOBAL_MARKERS = ["window.ethereum", "window.__firefox__", "window.solana"] as const;

/** Browser ResizeObserver delivery noise — not an app bug. */
const RESIZE_OBSERVER_NOISE = [
	"ResizeObserver loop completed with undelivered notifications.",
	"ResizeObserver loop limit exceeded",
] as const;

/**
 * Stripe Embedded Checkout wrapping WebKit's failed-fetch message.
 * Match this exact value only — do not drop other IntegrationErrors.
 */
const STRIPE_FETCH_CLIENT_SECRET_LOAD_FAILED = 'fetchClientSecret failed with error "Load failed"';

function exceptionValues(event: PosthogExceptionEventLike): string[] {
	const props = event.properties;
	if (!props) return [];

	const fromList =
		props.$exception_list
			?.map((item) => (typeof item?.value === "string" ? item.value : null))
			.filter((v): v is string => v != null) ?? [];
	if (fromList.length > 0) return fromList;

	const raw = props.$exception_values;
	if (Array.isArray(raw)) {
		return raw.filter((v): v is string => typeof v === "string");
	}
	if (typeof raw === "string") return [raw];
	return [];
}

function hasStackFrames(event: PosthogExceptionEventLike): boolean {
	const list = event.properties?.$exception_list;
	if (!list?.length) return false;
	return list.some((item) => {
		const frames = item?.stacktrace?.frames;
		return Array.isArray(frames) && frames.length > 0;
	});
}

function isOpaqueScriptError(value: string, event: PosthogExceptionEventLike): boolean {
	return value === "Script error." && !hasStackFrames(event);
}

function isInjectedBrowserGlobal(value: string): boolean {
	return INJECTED_GLOBAL_MARKERS.some((marker) => value.includes(marker));
}

function isResizeObserverNoise(value: string): boolean {
	return RESIZE_OBSERVER_NOISE.some((msg) => value.includes(msg));
}

function isStripeFetchClientSecretLoadFailed(value: string): boolean {
	return value === STRIPE_FETCH_CLIENT_SECRET_LOAD_FAILED;
}

/**
 * Returns true when this `$exception` is known browser/extension noise and
 * should be dropped in `before_send` (return null).
 */
export function isIgnoredBrowserException(event: PosthogExceptionEventLike): boolean {
	if (event.event !== "$exception") return false;

	const values = exceptionValues(event);
	if (values.length === 0) return false;

	return values.some(
		(value) =>
			isOpaqueScriptError(value, event) ||
			isInjectedBrowserGlobal(value) ||
			isResizeObserverNoise(value) ||
			isStripeFetchClientSecretLoadFailed(value),
	);
}
